<script>
import { Bar } from 'vue-chartjs';
import { chunk } from 'lodash';

export default {
    extends: Bar,
    props: {
        data: {
            type: [Boolean, Object],
            default: false,
        },
        small: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            monthNames: [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
            ],
            chartdata: {
                labels: [],
                datasets: [
                    {
                        label: 'Clicks',
                        backgroundColor: '#1f1f1f',
                        data: [],
                        xAxisID: 'bar-x-axis1',
                        barThickness: this.small ? 7 : 10,
                    },
                    {
                        label: 'Views',
                        backgroundColor: '#959595',
                        data: [],
                        xAxisID: 'bar-x-axis2',
                        barThickness: this.small ? 7 : 10,
                        categoryPercentage: 0.8,
                        barPercentage: 0.9,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            id: 'bar-x-axis1',
                            ticks: {
                                fontSize: this.small ? 10 : 12,
                            },
                        },
                        {
                            display: false,
                            stacked: true,
                            id: 'bar-x-axis2',
                            // these are needed because the bar controller defaults set only the first x axis properties
                            type: 'category',
                            gridLines: {
                                offsetGridLines: true,
                            },
                            offset: true,
                        },
                    ],
                    yAxes: [
                        {
                            stacked: false,
                            ticks: {
                                beginAtZero: true,
                                fontSize: this.small ? 10 : 12,
                            },
                        },
                    ],
                },
            },
        };
    },
    mounted() {
        this.prepareChartData();
        this.renderChart(this.chartdata, this.options);
    },
    computed: {
        hasViews() {
            return this.data.views_by_period !== undefined;
        },
    },
    methods: {
        formatDate(date) {
            const dateSplit = date.split('-');
            const year = parseInt(dateSplit[0]);
            const month = parseInt(dateSplit[1]);
            const day = parseInt(dateSplit[2]);

            return `${day}.${month}.${year}`;
        },
        prepareChartData() {
            const difference = this.dateDifference();

            // no views: remove second bar data
            if (!this.hasViews) this.chartdata.datasets.splice(1, 1);

            // show days in chart if less than 5 weeks
            if (difference.weeks < 5) {
                this.showDataInDays();
                // show weeks in chart if less than 4 months
            } else if (difference.months < 4) {
                this.showDataInWeeks();
                // months if less than 2 years
            } else if (difference.months < 24) {
                this.showDataInMonths();
                // years if more than two years
            } else {
                this.showDataInYears();
            }
        },
        showDataInDays() {
            Object.keys(this.data.clicks_by_period).forEach(date => {
                this.chartdata.labels.push(this.formatDate(date));
            });
            this.chartdata.datasets[0].data = Object.values(
                this.data.clicks_by_period,
            );
            if (this.hasViews) {
                this.chartdata.datasets[1].data = Object.values(
                    this.data.views_by_period,
                );
            }
        },
        showDataInWeeks() {
            const chunkSize = 7;

            const daysArray = Object.entries(
                this.data.clicks_by_period,
            ).map(([key, value]) => ({ [key]: value }));
            const weeks = chunk(daysArray, chunkSize);
            weeks.forEach(week => {
                const firstDayOfWeek = week[0];
                const lastDayOfWeek = week[week.length - 1];

                this.chartdata.labels.push(
                    `${this.formatDate(
                        Object.keys(firstDayOfWeek)[0],
                    )} – ${this.formatDate(Object.keys(lastDayOfWeek)[0])}`,
                );

                let clicks = 0;
                let views = 0;
                week.forEach(day => {
                    clicks += Object.values(day)[0];
                    if (this.hasViews)
                        views += this.data.views_by_period[Object.keys(day)[0]];
                });
                this.chartdata.datasets[0].data.push(clicks);
                if (this.hasViews) this.chartdata.datasets[1].data.push(views);
            });
        },
        showDataInMonths() {
            const dataByDate = this.prepareDataByDate();

            Object.keys(dataByDate).forEach(year => {
                const months = Object.keys(dataByDate[year]);

                months.forEach(month => {
                    this.chartdata.labels.push(
                        `${this.monthNames[month - 1]} ${year}`,
                    );

                    let clicks = 0;
                    let views = 0;
                    Object.values(dataByDate[year][month]).forEach(value => {
                        clicks += value.clicks;
                        if (this.hasViews) views += value.views;
                    });
                    this.chartdata.datasets[0].data.push(clicks);
                    if (this.hasViews)
                        this.chartdata.datasets[1].data.push(views);
                });
            });
        },
        showDataInYears() {
            const dataByDate = this.prepareDataByDate();
            Object.keys(dataByDate).forEach(year => {
                this.chartdata.labels.push(year);
            });
        },
        prepareDataByDate() {
            if (!this.data.clicks_by_period) return false;
            const days = Object.keys(this.data.clicks_by_period);

            const sortedDays = {};

            days.forEach(date => {
                const dateSplit = date.split('-');
                const year = parseInt(dateSplit[0]);
                const month = parseInt(dateSplit[1]);
                const day = parseInt(dateSplit[2]);

                if (!sortedDays[year]) {
                    sortedDays[year] = {};
                }
                if (!sortedDays[year][month]) {
                    sortedDays[year][month] = {};
                }
                if (!sortedDays[year][month][day]) {
                    sortedDays[year][month][day] = {
                        clicks: this.data.clicks_by_period[date],
                        views: this.data.views_by_period
                            ? this.data.views_by_period[date]
                            : false,
                    };
                }
            });

            return sortedDays;
        },
        dateDifference() {
            const second = 1000;
            const minute = second * 60;
            const hour = minute * 60;
            const day = hour * 24;
            const week = day * 7;
            if (!this.data.start_date) return false;
            const parsedStart = Date.parse(this.data.start_date);
            const parsedEnd = Date.parse(this.data.end_date);
            const timeDifference = parsedEnd - parsedStart;
            if (isNaN(timeDifference)) return false;
            return {
                years:
                    new Date(parsedEnd).getFullYear() -
                    new Date(parsedStart).getFullYear(),
                months:
                    new Date(parsedEnd).getFullYear() * 12 +
                    new Date(parsedEnd).getMonth() -
                    (new Date(parsedStart).getFullYear() * 12 +
                        new Date(parsedStart).getMonth()),
                weeks: Math.floor(timeDifference / week),
                days: timeDifference / day,
            };
        },
    },
};
</script>
